import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { AlertTriangle, X, Loader } from "lucide-react";
import Modal from "components/common/Modal";

const RevokeApiKeyModal = ({ show, onClose, onRevoke, apiKey }) => {
  const { t } = useTranslation();
  const [revoking, setRevoking] = useState(false);

  const handleClose = () => {
    if (revoking) return;
    setRevoking(false);
    onClose();
  };

  const handleRevoke = async () => {
    if (revoking) return;
    setRevoking(true);
    try {
      await onRevoke(apiKey);
      setRevoking(false);
      onClose();
    } catch {
      setRevoking(false);
    }
  };

  return (
    <Modal show={show} onClose={handleClose} maxWidth="420px">
      {/* Header - Red warning gradient */}
      <div
        className="relative overflow-hidden p-7"
        style={{
          background: "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)",
        }}
      >
        <div
          className="absolute rounded-full"
          style={{
            width: 120,
            height: 120,
            top: -30,
            right: -30,
            background: "rgba(255, 255, 255, 0.1)",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: 80,
            height: 80,
            bottom: -40,
            right: 60,
            background: "rgba(255, 255, 255, 0.08)",
          }}
        />

        <div className="relative flex items-start justify-between">
          <div>
            <div
              className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl"
              style={{ background: "rgba(255, 255, 255, 0.2)" }}
            >
              <AlertTriangle size={24} className="text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">
              {t("workspace.revokeApiKeyModal.title")}
            </h2>
            <p
              className="mt-1.5 text-sm"
              style={{ color: "rgba(255, 255, 255, 0.8)" }}
            >
              {t("workspace.shared.cannotBeUndone")}
            </p>
          </div>

          <button
            onClick={handleClose}
            className="flex h-9 w-9 items-center justify-center rounded-[10px] transition-colors duration-150"
            style={{ background: "rgba(255, 255, 255, 0.2)" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)")
            }
          >
            <X size={18} className="text-white" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-7">
        <p className="text-sm text-[#374151]">
          <Trans
            i18nKey="workspace.revokeApiKeyModal.message"
            values={{ name: apiKey?.name ?? "" }}
            components={{
              bold: <span className="font-semibold text-[#111827]" />,
            }}
          />
        </p>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 border-t border-[#F3F4F6] px-7 py-5">
        <button
          onClick={handleClose}
          disabled={revoking}
          className="rounded-[10px] border border-[#E5E7EB] bg-white px-5 py-2.5 text-sm font-medium text-[#374151] transition-colors duration-150 hover:bg-[#F9FAFB]"
        >
          {t("common.actions.cancel")}
        </button>
        <button
          onClick={handleRevoke}
          disabled={revoking}
          className="rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-150"
          style={{
            background: "#DC2626",
            boxShadow: "0 2px 8px rgba(220, 38, 38, 0.3)",
            cursor: revoking ? "wait" : "pointer",
          }}
        >
          {revoking ? (
            <span className="flex items-center gap-2">
              <Loader size={14} className="animate-spin" />
              {t("workspace.revokeApiKeyModal.revoking")}
            </span>
          ) : (
            t("workspace.revokeApiKeyModal.submit")
          )}
        </button>
      </div>
    </Modal>
  );
};

export default RevokeApiKeyModal;
